import {
  STUDY_VERDICT_EVIDENCE_ID,
  evidenceIndexById,
  formatCutGroupValue,
  isCutEvidenceId,
  readableCuts,
} from '@/features/studies/report/storyline'

import type {
  CutReadStrength,
  MetricResult,
  PublicStoryline,
  PublicStorylineFinding,
  QualitativeThemePackage,
  StorylineEvidenceEntry,
  StorylineEvidenceRef,
  StorylineEvidenceUnit,
} from '@digital-twins/shared-types/reporting'
import type { CutGroupResult, CutResult } from '@/features/studies/report/storyline'

type CutValueUnit = NonNullable<CutResult['valueUnit']>
type CutOutcomeRef = NonNullable<CutResult['outcome']>
type CutMembershipKind = NonNullable<CutGroupResult['membership']>['kind']

/**
 * The analyst tab's embedded plots, library-neutral (analyst page plan,
 * Step 4). This module decides WHICH findings plot and WHAT each plot shows,
 * from stored Python values only (cut groups, `bucket:n` breakdowns, option
 * shares, theme percents), never recomputed. `StorylineFigure` is the only
 * place that knows which chart components draw these shapes; at the
 * plots-stage1 merge only its internals change, and this seam stays.
 */

/** What the plots read from the served analysis. */
export interface StorylinePlotAnalysis {
  metricResults?: Readonly<Record<string, MetricResult>> | null
  qualitativeResults?: Readonly<Record<string, QualitativeThemePackage>> | null
}

export interface FindingPlotCutGroup {
  groupId: string
  label: string
  base: number
  /** Null when the group is counts only (under the directional floor). */
  value: number | null
  count: number | null
  thin: boolean
  /** The value at the cut's decimals, as the text rows print it. */
  formatted: string | null
}

export interface FindingPlotCut {
  requestId: string
  /** Short chip label; null when none can be named (no chips then). */
  chipLabel: string | null
  /** The chip's full text, for its tooltip. */
  chipTitle: string | null
  outcomeId: string | null
  /** The outcome in words (the metric short label, else its question). */
  outcomeLabel: string | null
  outcome: CutOutcomeRef | null
  membershipKind: CutMembershipKind | null
  groups: Array<FindingPlotCutGroup>
  unit: CutValueUnit
  decimals: number
  scaleMin: number | null
  scaleMax: number | null
  readStrength: CutReadStrength
}

export interface FindingPlotBucket {
  /** The breakdown's bucket id (`4`, `61-80`). */
  id: string
  /** The bucket's low edge, its place on the scale. */
  value: number
  min: number
  max: number
  label: string
  share: number
  isCited: boolean
}

export interface FindingPlotShareRow {
  key: string
  label: string
  share: number
  isCited: boolean
}

export interface FindingPlotThemeRow {
  themeId: string
  label: string
  share: number
}

export type FindingPlot =
  | { kind: 'cut'; cuts: Array<FindingPlotCut> }
  | {
      kind: 'rating'
      metricId: string
      assetId: string | null
      label: string
      scaleMin: number
      scaleMax: number
      buckets: Array<FindingPlotBucket>
      base: number
    }
  | {
      kind: 'shares'
      metricId: string
      assetId: string | null
      label: string
      rows: Array<FindingPlotShareRow>
      base: number
      unit: StorylineEvidenceUnit | null
    }
  | {
      kind: 'themes'
      qualId: string
      rows: Array<FindingPlotThemeRow>
      base: number
      unit: StorylineEvidenceUnit | null
    }

export type FindingPlotKind = FindingPlot['kind']

export interface PlottedFinding {
  findingId: string
  plot: FindingPlot
}

export const STORYLINE_PLOT_CAP = 3

const REAL_READS: ReadonlySet<CutReadStrength> = new Set<CutReadStrength>(['significant', 'directional'])
const RESIDUAL_THEME_SUFFIX = ':other_mixed'
const RESERVED_BREAKDOWN_PREFIXES = ['__', 'bucket:', 'bin:']
const BUCKET_PREFIX = 'bucket:'

/** A cited quant figure: the metric it reads and where in it. */
interface QuantRef {
  metricId: string
  assetId: string | null
  ref: StorylineEvidenceRef | null
  unit: StorylineEvidenceUnit | null
}

/** The ids that decide a plot: citation order, no verdict, no cut ids. */
function figureIds(finding: PublicStorylineFinding): Array<string> {
  return finding.evidenceIds.filter((id) => id !== STUDY_VERDICT_EVIDENCE_ID && !isCutEvidenceId(id))
}

/** The request ids a finding cites, from its cut requests and its `cut:` ids. */
function citedCutIds(finding: PublicStorylineFinding): Array<string> {
  const fromEvidence = finding.evidenceIds.filter(isCutEvidenceId).map((id) => id.slice('cut:'.length).split(':')[0])
  return Array.from(new Set([...(finding.cutRequestIds ?? []), ...fromEvidence]))
}

function hasRealReadCut(finding: PublicStorylineFinding, storyline: PublicStoryline): boolean {
  return readableCuts(storyline, citedCutIds(finding)).some((cut) =>
    REAL_READS.has(cut.comparison?.readStrength as CutReadStrength),
  )
}

// -- Cuts -------------------------------------------------------------------------

/** Signed indexes (NPS, any scale reaching below zero) stay as text in v1. */
function isSignedCut(cut: CutResult): boolean {
  if (cut.valueUnit === 'index') {
    return true
  }
  const scaleMin = cut.outcome?.scaleMin
  return typeof scaleMin === 'number' && scaleMin < 0
}

function isPlottableCut(cut: CutResult): boolean {
  if (!REAL_READS.has(cut.comparison?.readStrength as CutReadStrength) || isSignedCut(cut)) {
    return false
  }
  if (cut.valueUnit === 'scale_points') {
    const scaleMax = cut.outcome?.scaleMax
    if (typeof scaleMax !== 'number' || scaleMax <= 0) {
      return false
    }
  }
  return (cut.groups ?? []).some((group) => typeof group.value === 'number')
}

function metricShortLabel(metric: MetricResult | undefined): string | null {
  if (!metric) {
    return null
  }
  return metric.display.shortLabel?.trim() || metric.display.label?.trim() || null
}

function outcomeLabelOf(cut: CutResult, analysis: StorylinePlotAnalysis): string | null {
  const metric = cut.outcome ? analysis.metricResults?.[cut.outcome.metricId] : undefined
  return metricShortLabel(metric) ?? cut.outcome?.questionTitle?.trim() ?? null
}

/**
 * The interim chip label (until the plots-stage1 chart labels): the
 * producer's `splitLabel`, else by the first group's membership kind.
 */
function interimChipLabel(cut: CutResult, analysis: StorylinePlotAnalysis): string | null {
  const producer = cut.splitLabel?.trim()
  if (producer) {
    return producer
  }
  const membership = cut.groups?.[0]?.membership
  if (!membership) {
    const title = cut.outcome?.questionTitle?.trim()
    if (title) {
      return title
    }
    const items = cut.outcome?.itemLabels ?? []
    return items.length > 0 ? items.join(' / ') : null
  }
  switch (membership.kind) {
    case 'saved_segment':
    case 'not_in_segment':
      return membership.valueLabels?.[0]?.trim() || null
    case 'answered':
    case 'picked':
    case 'rated':
    case 'not_picked':
    case 'attribute_level': {
      const source = membership.sourceTitle?.trim()
      if (!source) {
        return null
      }
      const metric = Object.values(analysis.metricResults ?? {}).find(
        (candidate) => candidate.questionText === membership.sourceTitle,
      )
      return metric?.display.shortLabel?.trim() || source
    }
    default: {
      const unhandled: never = membership.kind
      return unhandled
    }
  }
}

function cutPlot(cuts: Array<CutResult>, analysis: StorylinePlotAnalysis): FindingPlot {
  const labels = cuts.map((cut) => interimChipLabel(cut, analysis))
  const plotted = cuts.map((cut, index): FindingPlotCut => {
    const outcomeLabel = outcomeLabelOf(cut, analysis)
    let chipLabel = labels[index]
    const isShared = chipLabel !== null && labels.filter((label) => label === chipLabel).length > 1
    if (isShared && outcomeLabel) {
      chipLabel = `${chipLabel} (${outcomeLabel})`
    }
    return {
      requestId: cut.requestId,
      chipLabel,
      chipTitle: chipLabel,
      outcomeId: cut.outcome?.metricId ?? null,
      outcomeLabel,
      outcome: cut.outcome ?? null,
      membershipKind: cut.groups?.[0]?.membership?.kind ?? null,
      groups: (cut.groups ?? []).map((group) => ({
        groupId: group.groupId,
        label: group.label,
        base: group.base,
        value: typeof group.value === 'number' ? group.value : null,
        count: typeof group.count === 'number' ? group.count : null,
        thin: group.thin,
        formatted: formatCutGroupValue(cut, group),
      })),
      unit: cut.valueUnit as CutValueUnit,
      decimals: cut.displayDecimals as number,
      scaleMin: cut.outcome?.scaleMin ?? null,
      scaleMax: cut.outcome?.scaleMax ?? null,
      readStrength: cut.comparison?.readStrength as CutReadStrength,
    }
  })
  return { kind: 'cut', cuts: plotted }
}

// -- Quant ------------------------------------------------------------------------

function quantRef(
  id: string,
  entry: StorylineEvidenceEntry | undefined,
  analysis: StorylinePlotAnalysis,
): QuantRef | null {
  if (entry) {
    if ((entry.kind === 'metric' || entry.kind === 'composite') && entry.metricId) {
      return {
        metricId: entry.metricId,
        assetId: entry.assetId ?? null,
        ref: entry.ref,
        unit: entry.unit ?? null,
      }
    }
    return null
  }
  if (analysis.metricResults?.[id]) {
    return { metricId: id, assetId: null, ref: null, unit: null }
  }
  return null
}

/** The metric a cited figure reads: pooled, or the cited asset's own result. */
function scopedMetric(quant: QuantRef, analysis: StorylinePlotAnalysis): MetricResult | null {
  const metric = analysis.metricResults?.[quant.metricId]
  if (!metric) {
    return null
  }
  const scope = quant.ref?.scope ?? 'pooled'
  if (quant.ref?.derived) {
    // A post or lift half of a paired read is not a distribution.
    return null
  }
  switch (scope) {
    case 'pooled':
      return metric
    case 'asset':
      return quant.assetId ? (metric.perAsset?.[quant.assetId] ?? null) : null
    case 'row':
    case 'cell':
      // Row and cell reads have no stored distribution of their own here.
      return null
    default: {
      const unhandled: never = scope
      return unhandled
    }
  }
}

function parseBucket(id: string): { min: number; max: number } | null {
  const range = /^(-?\d+(?:\.\d+)?)-(-?\d+(?:\.\d+)?)$/.exec(id)
  if (range) {
    return { min: Number(range[1]), max: Number(range[2]) }
  }
  const point = Number(id)
  return id.trim() !== '' && Number.isFinite(point) ? { min: point, max: point } : null
}

/**
 * Which buckets the cited band marks: only when the band covers whole
 * buckets exactly (point buckets, or bucket edges equal to the band edges).
 * A band that cuts a bucket marks nothing.
 */
function citedBuckets(
  buckets: Array<{ min: number; max: number }>,
  band: { min: number; max: number } | null | undefined,
): Array<boolean> {
  const none = buckets.map(() => false)
  if (!band) {
    return none
  }
  const inside = buckets.map((bucket) => bucket.min >= band.min && bucket.max <= band.max)
  const cutsABucket = buckets.some(
    (bucket, index) => !inside[index] && bucket.max >= band.min && bucket.min <= band.max,
  )
  const covered = buckets.filter((_, index) => inside[index])
  if (cutsABucket || covered.length === 0) {
    return none
  }
  const low = Math.min(...covered.map((bucket) => bucket.min))
  const high = Math.max(...covered.map((bucket) => bucket.max))
  return low === band.min && high === band.max ? inside : none
}

function ratingPlot(quant: QuantRef, metric: MetricResult): FindingPlot | null {
  const breakdown = metric.breakdown ?? {}
  const parsed = Object.entries(breakdown)
    .filter(([key, share]) => key.startsWith(BUCKET_PREFIX) && typeof share === 'number')
    .map(([key, share]) => {
      const id = key.slice(BUCKET_PREFIX.length)
      return { id, share, edges: parseBucket(id) }
    })
  if (parsed.length === 0 || parsed.some((bucket) => bucket.edges === null)) {
    return null
  }
  const ordered = parsed
    .map((bucket) => ({
      id: bucket.id,
      share: bucket.share,
      min: bucket.edges!.min,
      max: bucket.edges!.max,
    }))
    .sort((left, right) => left.min - right.min)
  const cited = citedBuckets(ordered, quant.ref?.band)
  return {
    kind: 'rating',
    metricId: quant.metricId,
    assetId: quant.assetId,
    label: metricShortLabel(metric) ?? quant.metricId,
    scaleMin: ordered[0].min,
    scaleMax: ordered[ordered.length - 1].max,
    buckets: ordered.map((bucket, index) => ({
      id: bucket.id,
      value: bucket.min,
      min: bucket.min,
      max: bucket.max,
      label: bucket.id,
      share: bucket.share,
      isCited: cited[index],
    })),
    base: metric.baseSize,
  }
}

function sharesPlot(quant: QuantRef, metric: MetricResult): FindingPlot | null {
  const breakdown = metric.breakdown ?? {}
  if (Object.keys(breakdown).some((key) => key.startsWith('bin:'))) {
    return null
  }
  const optionOrder = Object.keys(metric.optionLabels ?? {})
  const keys = Object.keys(breakdown).filter(
    (key) =>
      typeof breakdown[key] === 'number' && !RESERVED_BREAKDOWN_PREFIXES.some((prefix) => key.startsWith(prefix)),
  )
  if (keys.length === 0) {
    return null
  }
  const rank = (key: string) => {
    const index = optionOrder.indexOf(key)
    return index === -1 ? optionOrder.length : index
  }
  const ordered = keys.slice().sort((left, right) => rank(left) - rank(right))
  const citedKey = quant.ref?.optionKey ?? null
  return {
    kind: 'shares',
    metricId: quant.metricId,
    assetId: quant.assetId,
    label: metricShortLabel(metric) ?? quant.metricId,
    rows: ordered.map((key) => ({
      key,
      label: metric.optionLabels?.[key] ?? key,
      share: breakdown[key],
      isCited: citedKey !== null && key === citedKey,
    })),
    base: metric.baseSize,
    unit: quant.unit,
  }
}

function quantPlot(quant: QuantRef, analysis: StorylinePlotAnalysis): FindingPlot | null {
  const metric = scopedMetric(quant, analysis)
  if (!metric) {
    return null
  }
  const hasBuckets = Object.keys(metric.breakdown ?? {}).some((key) => key.startsWith(BUCKET_PREFIX))
  return hasBuckets ? ratingPlot(quant, metric) : sharesPlot(quant, metric)
}

// -- Themes -----------------------------------------------------------------------

function themePackageId(
  id: string,
  entry: StorylineEvidenceEntry | undefined,
  analysis: StorylinePlotAnalysis,
): string | null {
  if (entry) {
    return entry.kind === 'qual_theme' && entry.qualId ? entry.qualId : null
  }
  for (const [qualId, pack] of Object.entries(analysis.qualitativeResults ?? {})) {
    if (pack.themes.some((theme) => theme.id === id)) {
      return qualId
    }
  }
  return null
}

function themesPlot(
  ids: Array<string>,
  index: ReadonlyMap<string, StorylineEvidenceEntry> | null,
  analysis: StorylinePlotAnalysis,
): FindingPlot | null {
  const packageIds = new Set(ids.map((id) => themePackageId(id, index?.get(id), analysis)))
  if (packageIds.size !== 1 || packageIds.has(null)) {
    return null
  }
  const qualId = Array.from(packageIds)[0] as string
  const pack = analysis.qualitativeResults?.[qualId]
  if (!pack || pack.sharedCodeframe !== true || pack.variantBasis != null) {
    return null
  }
  const entries = ids.map((id) => index?.get(id)).filter((entry): entry is StorylineEvidenceEntry => Boolean(entry))
  if (entries.some((entry) => entry.unit === 'quotes')) {
    return null
  }
  const cited = new Set(ids.filter((id) => !id.endsWith(RESIDUAL_THEME_SUFFIX)))
  const rows = pack.themes
    .filter((theme) => cited.has(theme.id) && typeof theme.percent === 'number')
    .map((theme) => ({
      themeId: theme.id,
      label: theme.title,
      share: theme.percent,
    }))
  if (rows.length === 0) {
    return null
  }
  const firstBase = entries.find((entry) => typeof entry.base === 'number')?.base
  return {
    kind: 'themes',
    qualId,
    rows,
    base: typeof firstBase === 'number' ? firstBase : pack.responseCount,
    unit: entries.find((entry) => entry.unit)?.unit ?? null,
  }
}

// -- Public -----------------------------------------------------------------------

/**
 * The plot a finding carries, or null. Evidence in citation order,
 * `study_verdict` ignored: cited readable cuts with a real read (significant
 * or directional, not a signed index) plot as cuts; else the first quant id
 * decides (rating or shares, quant beats qual); else themes, when every id is
 * a theme of one pooled shared-codeframe package (residual bucket excluded,
 * only the cited themes). Every figure is a stored value.
 */
export function findingPlot(
  finding: PublicStorylineFinding,
  storyline: PublicStoryline,
  analysis: StorylinePlotAnalysis,
): FindingPlot | null {
  const index = evidenceIndexById(storyline)
  // A group cell is not the pooled distribution behind its metric. Its exact
  // figures remain in the existing source rows until a table chart is supported.
  if (finding.evidenceIds.some((id) => index?.get(id)?.tableCellRef != null)) {
    return null
  }
  const cuts = readableCuts(storyline, citedCutIds(finding)).filter(isPlottableCut)
  if (cuts.length > 0) {
    return cutPlot(cuts, analysis)
  }
  const ids = figureIds(finding)
  for (const id of ids) {
    const quant = quantRef(id, index?.get(id), analysis)
    if (quant) {
      return quantPlot(quant, analysis)
    }
  }
  return ids.length > 0 ? themesPlot(ids, index, analysis) : null
}

function isQuantPlot(plot: FindingPlot): boolean {
  return plot.kind === 'rating' || plot.kind === 'shares'
}

/**
 * Which findings plot, at most `cap`, in selection order:
 * 1. findings citing any cut whose read is significant or directional;
 * 2. findings whose evidence overlaps the thesis evidence (`study_verdict`
 *    excluded on both sides);
 * 3. the first remaining quant finding.
 * Inside a tier: a plot kind not yet selected first, then storyline order.
 * A finding without a plot is never selected.
 */
export function selectPlottedFindings(
  storyline: PublicStoryline,
  analysis: StorylinePlotAnalysis,
  cap: number = STORYLINE_PLOT_CAP,
): Array<PlottedFinding> {
  const candidates = storyline.findings
    .map((finding) => ({
      finding,
      plot: findingPlot(finding, storyline, analysis),
    }))
    .filter((candidate): candidate is { finding: PublicStorylineFinding; plot: FindingPlot } => candidate.plot !== null)
  const thesisIds = new Set(storyline.thesisEvidenceIds.filter((id) => id !== STUDY_VERDICT_EVIDENCE_ID))
  const selected: Array<PlottedFinding> = []
  const taken = new Set<string>()

  function takeFromTier(tier: Array<{ finding: PublicStorylineFinding; plot: FindingPlot }>, limit: number) {
    const pool = tier.filter((candidate) => !taken.has(candidate.finding.findingId))
    let added = 0
    while (pool.length > 0 && selected.length < cap && added < limit) {
      const kinds = new Set(selected.map((entry) => entry.plot.kind))
      const pickIndex = Math.max(
        0,
        pool.findIndex((candidate) => !kinds.has(candidate.plot.kind)),
      )
      const [pick] = pool.splice(pickIndex, 1)
      taken.add(pick.finding.findingId)
      selected.push({ findingId: pick.finding.findingId, plot: pick.plot })
      added += 1
    }
  }

  takeFromTier(
    candidates.filter((candidate) => hasRealReadCut(candidate.finding, storyline)),
    Number.POSITIVE_INFINITY,
  )
  takeFromTier(
    candidates.filter((candidate) =>
      candidate.finding.evidenceIds.some((id) => id !== STUDY_VERDICT_EVIDENCE_ID && thesisIds.has(id)),
    ),
    Number.POSITIVE_INFINITY,
  )
  takeFromTier(
    candidates.filter((candidate) => isQuantPlot(candidate.plot)),
    1,
  )
  return selected
}
